/**
 * The kitchen programme — Megan's approved kit, 2026-10-18, over 11 review
 * rounds of the prototype. ORDER IS PRECEDENCE: when the budget is tight the
 * planner drops from the bottom, optional props first. A kitchen table of its
 * own, deliberately not the bathroom's `PROP_PROGRAM`: that table's general
 * props (a tray, art, dried stems) are bathroom choices and would leak in.
 *
 * `where` is a closed set — every value has a stage in `index.js`, and a test
 * fails on one that does not:
 *   'island-or-run'    on the island if there is one, else a wall run
 *   'counter'          on a wall run or island, near its anchor
 *   'backsplash-lean'  at the back edge of a WALL-BACKED run, leaning on it
 *   'appliance-handle' hung on a procedural range's oven handle
 *
 * `near` is a closed set too, naming what the prop is placed beside:
 *   'fruit'  the fruit bowl     'sink'  the sink       'soap'  the soap set
 *   'range'  the range's landing counter
 *   'fridge' the counter end nearest the fridge
 *   'away-from-sink'  the run end farthest from the sink
 *
 * Sources for each prop are in the review artifact's plan table and in
 * claude-docs/2026-09-27-open-house-staging-research.md (Emily Henderson's
 * kitchen list; HomeLight; Apartment Therapy).
 */

const KITCHEN_PROP_PROGRAM = [
  { type: 'staging-fruit-bowl', where: 'island-or-run', count: 1 },
  {
    type: 'staging-vase-tulips',
    where: 'island-or-run',
    near: 'fruit',
    count: 1,
  },
  { type: 'staging-dish-soap-set', where: 'counter', near: 'sink', count: 1 },
  {
    type: 'staging-cutting-boards',
    where: 'backsplash-lean',
    near: 'range',
    count: 1,
  },
  { type: 'staging-utensil-crock', where: 'counter', near: 'range', count: 1 },
  // On a procedural range's handle; a folded dish towel beside the sink when
  // the range is a catalog model (Megan's rule). The fallback takes this rank.
  { type: 'staging-tea-towel', where: 'appliance-handle', count: 1 },
  // Salt and pepper live by the stove: beside the crock, the cooking station.
  {
    type: 'staging-mills',
    where: 'counter',
    near: 'range',
    count: 1,
    optional: true,
  },
  {
    type: 'staging-herb-pot',
    where: 'counter',
    near: 'soap',
    count: 1,
    optional: true,
  },
  {
    type: 'staging-cookbook-stand',
    where: 'backsplash-lean',
    near: 'away-from-sink',
    count: 1,
    optional: true,
  },
  // One small framed print leaned on the backsplash, the way stagers dress a
  // kitchen wall that is mostly cabinets; hung on an open kitchen wall instead
  // when no stretch of backsplash takes it, or when the counter has no room
  // left for it. Ranked below the herb pot and the cookbook: those are the
  // props stagers use to show the kitchen being cooked in, and a print can
  // move to the wall where they cannot. Optional.
  {
    type: 'staging-art-leaned',
    where: 'backsplash-lean',
    count: 1,
    optional: true,
  },
  {
    type: 'staging-canisters',
    where: 'backsplash-lean',
    near: 'fridge',
    count: 1,
    optional: true,
  },
];

const KITCHEN_WHERE = new Set([
  'island-or-run',
  'counter',
  'backsplash-lean',
  'appliance-handle',
]);
const KITCHEN_NEAR = new Set([
  'fruit',
  'sink',
  'soap',
  'range',
  'fridge',
  'away-from-sink',
]);

/** The literature's range is "1-2 styled items per counter run"; 8 is the kit's cap. */
const MAX_PROPS_PER_KITCHEN = 8;
/** At most this share of the kitchen's counter LENGTH may carry props (70% stays bare). */
const MAX_COUNTER_SHARE = 0.3;
/**
 * ...and at most this share of any ONE counter's. The kitchen-wide share alone
 * let four props crowd the 67 in run beside a Golden West range while three
 * wall runs stood bare.
 */
const MAX_RUN_SHARE = 0.5;

const IN = 0.0254;
/** How far from its anchor a `near` prop may stand, measured from the anchor's rim or end. */
const KITCHEN_NEAR_REACH_M = {
  sink: 18 * IN,
  soap: 6 * IN,
  range: 15 * IN,
  fruit: 10 * IN,
  crock: 6 * IN,
};
/** A prop clears whatever hangs over it by at least this much. */
const HEADROOM_CLEARANCE_M = 0.5 * IN;
/**
 * An island shallower than this is a seating LEDGE (a peninsula's 12 in bar
 * overhang), not a table: the fruit and flowers want the counter proper.
 */
const CENTREPIECE_MIN_DEPTH_M = 18 * IN;
/**
 * On an island the fruit and flowers are ONE compact grouping a third of the
 * way from an end, the other two thirds left open (Creatively Living,
 * "Kitchen island styling ideas"; and Megan's reference photo, 2026-10-18).
 */
const ISLAND_GROUP_FROM_END = 1 / 3;
/** The gap between the two pieces of a grouping (the fruit beside the flowers). */
const GROUP_GAP_M = 1 * IN;
/**
 * Bare counter kept between one grouping and the next along a stretch of
 * counter. Stagers set groupings apart and never line them up (Homestyler,
 * "Effective kitchen counter decoration ideas"; Homemade Lovely, "How to
 * create a vignette"); 50 Park's crock, mills and fruit stood in one line,
 * the fruit 12 in from the mills (Megan, 2026-10-18).
 */
const GROUP_SPACING_M = 12 * IN;
/**
 * Which grouping each prop belongs to. GROUP_SPACING_M is kept from props of
 * OTHER groupings only: the herb pot stands beside its soap, the mills beside
 * their crock, and the boards on the range's other landing are the same
 * cooking station.
 */
const KITCHEN_GROUPING = {
  'staging-fruit-bowl': 'centrepiece',
  'staging-vase-tulips': 'centrepiece',
  'staging-dish-soap-set': 'sink',
  'staging-herb-pot': 'sink',
  'staging-dish-towel-folded': 'sink',
  'staging-utensil-crock': 'range',
  'staging-mills': 'range',
  'staging-cutting-boards': 'range',
  'staging-tea-towel': 'range',
  // The stove itself is the cooking station's centre: nothing from another
  // grouping crowds it (50 Park: the fruit and flowers took the bare slab
  // beside the cooktop once the crock moved to its other side).
  range: 'range',
  stove: 'range',
  cooktop: 'range',
  'staging-cookbook-stand': 'cookbook',
  'staging-canisters': 'canisters',
  'staging-art-leaned': 'art',
  'staging-art': 'art',
};
/**
 * Kitchen art keeps this far from a range, cooktop or sink: steam and splashes
 * (Homestyler, Minted: 24-30 in).
 */
const ART_STEAM_CLEARANCE_M = 24 * IN;
/**
 * Hung kitchen art goes within this much of a counter, sink, range, cooktop
 * or fridge, measured in plan from the piece's centre — or not at all.
 *
 * The kitchen REGION alone does not say it. In an open-plan room that region
 * is the kitchen label's nearest-point share of the floor, which knows nothing
 * of where the cabinets are, so it reached a hallway stub wall 3.3 m from the
 * nearest counter (819 Montrose). On the corpus, art in a plain kitchen hangs
 * 0.7-1.8 m from the nearest fixture and art in an open plan 2.5-4.1 m, so
 * 2 m keeps every plain kitchen's piece and drops only the drifted ones.
 */
const KITCHEN_ART_REACH_M = 2.0;
/** Two cutting boards need this much clear backsplash. */
const LEAN_MIN_RUN_M = 14 * IN;

module.exports = {
  KITCHEN_PROP_PROGRAM,
  KITCHEN_WHERE,
  KITCHEN_NEAR,
  MAX_PROPS_PER_KITCHEN,
  MAX_COUNTER_SHARE,
  MAX_RUN_SHARE,
  KITCHEN_NEAR_REACH_M,
  HEADROOM_CLEARANCE_M,
  CENTREPIECE_MIN_DEPTH_M,
  ISLAND_GROUP_FROM_END,
  GROUP_GAP_M,
  GROUP_SPACING_M,
  KITCHEN_GROUPING,
  ART_STEAM_CLEARANCE_M,
  KITCHEN_ART_REACH_M,
  LEAN_MIN_RUN_M,
};
